/**
 * Training Recommendation Service
 * Handles training recommendation generation and management
 */
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { createErrorLogger, ErrorType } from '../utils/logger';

const logger = createErrorLogger('RecommendationService');

const RecommendationService = {
  /**
   * Generate training recommendations based on CV, job, and company analyses
   * @param {string} userId - The user ID to generate recommendations for
   * @returns {Promise<Object>} Object containing immediate, future, and AI recommendations
   */
  generateRecommendations: async (userId) => {
    logger.info('generateRecommendations', `Generating training recommendations for user: ${userId}`, { userId });

    try {
      // Send JSON data as expected by the backend TrainingRequest model
      const requestData = {
        user_id: userId
        // cv_data, job_data, company_data are optional - backend will load from files
      };

      const response = await axios.post(
        `${API_BASE_URL}/training/recommend`,
        requestData,
        { headers: { 'Content-Type': 'application/json' } }
      );

      const { result } = response.data;

      if (!result) {
        throw new Error('Training recommendation API returned an empty result payload.');
      }

      // Handle different response formats
      let recommendations = {};

      if (typeof result === 'string') {
        try {
          recommendations = JSON.parse(result);
        } catch (parseError) {
          logger.error('generateRecommendations', 'Failed to parse recommendations string', { parseError });
          throw new Error(`Invalid recommendation payload: ${parseError.message}`);
        }
      } else if (typeof result === 'object') {
        recommendations = result;
      }

      // The backend returns result.recommendations containing the actual recommendation arrays
      const recommendationData = recommendations.recommendations || recommendations;

      // Helper function to map backend recommendation fields to frontend expected fields (same as in loadTrainingRecommendations)
      const mapRecommendationFields = (backendRecs) => {
        return backendRecs.map(rec => ({
          // Keep all original fields
          ...rec,
          // Map backend fields to frontend expected field names
          reason: rec.description || rec.reason || '', // UI expects 'reason', backend has 'description'
          suggested_resources: rec.resource ? [rec.resource] : (rec.suggested_resources || []), // UI expects array, backend has string
          // Add additional context from backend fields
          additional_info: {
            addresses_skill_gaps: rec.addresses_skill_gaps || [],
            format_duration: rec.format_duration || '',
            evaluation_method: rec.evaluation_method || '',
            tracking_metrics: rec.tracking_metrics || ''
          }
        }));
      };

      // Ensure we have the expected structure - map backend field names to frontend names
      const structuredRecommendations = {
        immediate: mapRecommendationFields(
          recommendationData.immediate_recommendations ||
          recommendationData.immediate ||
          []
        ),
        future: mapRecommendationFields(recommendationData.future_recommendations || recommendationData.future || []),
        ai: mapRecommendationFields(recommendationData.ai_augmentation_recommendations || recommendationData.ai || [])
      };

      logger.info('generateRecommendations', 'Recommendations generated successfully', {
        immediateCount: structuredRecommendations.immediate.length,
        futureCount: structuredRecommendations.future.length,
        aiCount: structuredRecommendations.ai.length
      });

      return structuredRecommendations;
    } catch (error) {
      logger.error('generateRecommendations', 'Error generating recommendations', error, ErrorType.API_CALL);

      // Enhanced error handling with specific messages for different scenarios
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;

        // Handle 422 validation errors (Pydantic validation)
        if (status === 422) {
          logger.warn('generateRecommendations', 'Validation error - likely missing or invalid data', { errorData });
          throw new Error('Invalid request format. Please ensure all required data is provided.');
        }

        // Handle 400 bad request errors or 500 errors containing 400-level messages
        if (status === 400 || status === 500) {
          const errorMessage = errorData?.detail || errorData?.error || errorData?.message || 'Request error';

          // Check for specific missing data scenarios in the error message
          if (errorMessage.includes('CV analysis data is required')) {
            logger.warn('generateRecommendations', 'CV analysis data missing', { errorMessage });
            throw new Error('CV analysis data is required. Please complete the CV analysis first.');
          }

          if (errorMessage.includes('Job analysis data is required')) {
            logger.warn('generateRecommendations', 'Job analysis data missing', { errorMessage });
            throw new Error('Job analysis data is required. Please complete the job analysis first.');
          }

          if (errorMessage.includes('Company research data is required')) {
            logger.warn('generateRecommendations', 'Company research data missing', { errorMessage });
            throw new Error('Company research data is required. Please complete the company research first.');
          }

          // Check for general missing data scenarios
          if (errorMessage.includes('CV') || errorMessage.includes('cv_analyzer')) {
            logger.warn('generateRecommendations', 'CV-related error', { errorMessage });
            throw new Error('CV analysis data is required. Please complete the CV analysis first.');
          }

          if (errorMessage.includes('job') || errorMessage.includes('job_analyzer')) {
            logger.warn('generateRecommendations', 'Job-related error', { errorMessage });
            throw new Error('Job analysis data is required. Please complete the job analysis first.');
          }

          if (errorMessage.includes('company') || errorMessage.includes('company_research')) {
            logger.warn('generateRecommendations', 'Company-related error', { errorMessage });
            throw new Error('Company research data is required. Please complete the company research first.');
          }

          // If it's a 500 error without specific data requirements, treat as server error
          if (status === 500) {
            logger.error('generateRecommendations', 'Server error during recommendation generation', { errorMessage });
            throw new Error(`Server error: ${errorMessage}`);
          }

          // Generic 400 error
          throw new Error(errorMessage);
        }

        // Handle other HTTP errors
        const errorMessage = errorData?.detail || errorData?.message || `HTTP ${status} error`;
        throw new Error(errorMessage);
      }

      // Handle network errors or other issues
      if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        throw new Error('Network error. Please check your connection and try again.');
      }

      throw new Error(error.message || 'An unexpected error occurred while generating recommendations.');
    }
  },

  /**
   * Load training recommendations directly from disk
   * @param {string} userId - The user ID to load recommendations for
   * @returns {Promise<Object>} Object containing recommendation data or null if no data exists
   */
  loadTrainingRecommendations: async (userId) => {
    logger.info('loadTrainingRecommendations', `Loading training recommendations for user: ${userId}`, { userId });

    try {
      const response = await axios.get(`${API_BASE_URL}/training/load/${userId}`);
      const { result } = response.data;

      if (!result) {
        logger.info('loadTrainingRecommendations', 'No training recommendations found on disk', { userId });
        return null; // Return null instead of empty structure to indicate no data
      }

      // The backend returns result.recommendations containing the actual recommendation arrays
      const recommendationData = result.recommendations || result;

      // Helper function to map backend recommendation fields to frontend expected fields
      const mapRecommendationFields = (backendRecs) => {
        return backendRecs.map(rec => ({
          // Keep all original fields
          ...rec,
          // Map backend fields to frontend expected field names
          reason: rec.description || rec.reason || '', // UI expects 'reason', backend has 'description'
          suggested_resources: rec.resource ? [rec.resource] : (rec.suggested_resources || []), // UI expects array, backend has string
          // Add additional context from backend fields
          additional_info: {
            addresses_skill_gaps: rec.addresses_skill_gaps || [],
            format_duration: rec.format_duration || '',
            evaluation_method: rec.evaluation_method || '',
            tracking_metrics: rec.tracking_metrics || ''
          }
        }));
      };

      // Ensure result has expected structure - map backend field names to frontend names
      const structuredResult = {
        immediate_recommendations: mapRecommendationFields(
          recommendationData.immediate_recommendations ||
          recommendationData.immediate ||
          []
        ),
        future_recommendations: mapRecommendationFields(
          recommendationData.future_recommendations ||
          recommendationData.short_term_plan ||
          recommendationData.future ||
          []
        ),
        ai_augmentation_recommendations: mapRecommendationFields(
          recommendationData.ai_augmentation_recommendations ||
          recommendationData.long_term_goals ||
          recommendationData.ai ||
          []
        )
      };

      logger.info('loadTrainingRecommendations', 'Training recommendations loaded successfully', {
        userId,
        immediate: structuredResult.immediate_recommendations.length,
        future: structuredResult.future_recommendations.length,
        ai: structuredResult.ai_augmentation_recommendations.length
      });

      return structuredResult;
    } catch (error) {
      logger.info('loadTrainingRecommendations', 'No training recommendations file found - this is normal for new users', {
        userId,
        status: error.response?.status
      });

      // Return null for 404 (file not found) - this is expected for users without recommendations
      if (error.response?.status === 404) {
        return null;
      }

      // For other errors, still return null but log as warning
      logger.warn('loadTrainingRecommendations', 'Error loading training recommendations', {
        error: error.message,
        userId,
        status: error.response?.status
      });
      throw error;
    }
  },

  /**
   * Save training recommendations data to disk via API
   * @param {string} userId - The user ID to save recommendations for
   * @param {Object} recommendationsData - The recommendations data to save
   * @returns {Promise<Object>} The saved recommendations data
   */
  saveTrainingRecommendations: async (userId, recommendationsData) => {
    logger.info('saveTrainingRecommendations', `Saving training recommendations for user: ${userId}`, { userId });

    try {
      // For now, this is just a placeholder that logs and returns the data
      // In a full implementation, this would make an API call to save the data
      logger.info('saveTrainingRecommendations', 'Training recommendations saved successfully', {
        userId,
        immediateCount: recommendationsData.immediate?.length || 0,
        futureCount: recommendationsData.future?.length || 0,
        aiCount: recommendationsData.ai?.length || 0
      });

      return recommendationsData;
    } catch (error) {
      logger.error('saveTrainingRecommendations', 'Error saving training recommendations', error, ErrorType.DATA_SAVE);
      throw error;
    }
  }
};

export default RecommendationService;
